import { motion } from 'framer-motion';
import { Heart, Sparkles, Clock, Bell, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const CharityPage = () => {
  return (
    <main className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-midnight-400 hover:text-cyclara-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 rounded-3xl bg-gradient-to-br from-cyclara-500 to-lavender-500
                      flex items-center justify-center mx-auto mb-6 shadow-xl shadow-cyclara-500/30"
          >
            <Heart className="w-12 h-12 text-white" />
          </motion.div>

          <span className="inline-block px-4 py-2 rounded-full bg-lavender-100 text-lavender-600
                        text-sm font-medium mb-4">
            Giving Back
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-4">
            Cyclara for <span className="gradient-text">Charity</span>
          </h1>
          <p className="text-xl text-midnight-400 max-w-2xl mx-auto">
            Supporting women's health initiatives and menstrual equity organizations around the world.
          </p>
        </motion.div>

        {/* Coming Soon Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl shadow-xl shadow-midnight-500/5 border border-cyclara-100 p-8 md:p-12 text-center"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100
                      flex items-center justify-center mx-auto mb-6"
          >
            <Clock className="w-10 h-10 text-amber-600" />
          </motion.div>

          <h2 className="text-3xl font-bold text-midnight-500 mb-4">
            Coming Soon
          </h2>
          <p className="text-lg text-midnight-400 mb-8 max-w-lg mx-auto">
            We're building a platform to support amazing charities focused on menstrual health,
            period poverty, and women's wellness initiatives.
          </p>

          {/* Features Preview */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="p-6 rounded-2xl bg-cyclara-50 border border-cyclara-100">
              <Sparkles className="w-8 h-8 text-cyclara-500 mx-auto mb-3" />
              <h3 className="font-semibold text-midnight-500 mb-2">Verified Charities</h3>
              <p className="text-sm text-midnight-400">
                Partner with trusted organizations making real impact
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-lavender-50 border border-lavender-100">
              <Heart className="w-8 h-8 text-lavender-500 mx-auto mb-3" />
              <h3 className="font-semibold text-midnight-500 mb-2">Direct Donations</h3>
              <p className="text-sm text-midnight-400">
                100% of your donation goes directly to the cause
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-green-50 border border-green-100">
              <Bell className="w-8 h-8 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-midnight-500 mb-2">Impact Updates</h3>
              <p className="text-sm text-midnight-400">
                See exactly how your contribution helps
              </p>
            </div>
          </div>

          {/* Notify Form */}
          <div className="max-w-md mx-auto">
            <p className="text-midnight-500 font-medium mb-4">
              Want to be notified when we launch?
            </p>
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl border border-cyclara-200
                         focus:outline-none focus:ring-2 focus:ring-cyclara-500"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-to-r from-cyclara-500 to-lavender-500
                         text-white font-semibold rounded-xl shadow-lg shadow-cyclara-500/25
                         hover:shadow-xl hover:shadow-cyclara-500/30 transition-all"
              >
                Notify Me
              </motion.button>
            </div>
            <p className="text-sm text-midnight-300 mt-3">
              We'll only email you when the charity platform launches.
            </p>
          </div>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-midnight-400 max-w-2xl mx-auto">
            At Cyclara, we believe every person deserves access to menstrual health resources.
            Our upcoming charity platform will connect you with organizations fighting period poverty
            and advancing women's health worldwide.
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default CharityPage;
